<template>
  <el-dialog
    v-model="visible"
    :title="translate('Request an upgrade')"
    width="460px"
    append-to-body
    :close-on-click-modal="false">
    <p class="ur-description">
      {{ translate("Tell us roughly how big your operation is — estimates are fine, the numbers don't have to be exact. We'll use them to recommend the right plan and get back to you.") }}
    </p>

    <el-form
      ref="form"
      :model="form"
      label-position="top"
      @submit.prevent>
      <el-form-item :label="translate('Number of users')">
        <el-input-number v-model="form.users" :min="0" controls-position="right" class="ur-number" />
      </el-form-item>
      <el-form-item :label="translate('Number of workcenters')">
        <el-input-number v-model="form.workcenters" :min="0" controls-position="right" class="ur-number" />
      </el-form-item>
      <el-form-item :label="translate('Number of workers')">
        <el-input-number v-model="form.workers" :min="0" controls-position="right" class="ur-number" />
      </el-form-item>
      <el-form-item :label="translate('Industry')">
        <el-input
          v-model="form.industry"
          :placeholder="translate('e.g. Metal fabrication, Furniture, Electronics')"
          maxlength="120"
          show-word-limit />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="visible = false">{{ translate('Cancel') }}</el-button>
      <el-button type="primary" :loading="sending" @click="submit">
        {{ translate('Request upgrade') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'

// Shared "Request an upgrade" dialog (free-tier feature). Mounted once at the
// app root (Root.vue) and opened via the event bus from the tier-limit prompts
// (tierGuard mixin + 402 interceptor) and the Plan & Billing page. On submit it
// POSTs to the existing account endpoint, which emails the platform inbox along
// with the requesting tenant admin's registration email.
export default {
  name: 'CcUpgradeRequest',

  data() {
    return {
      visible: false,
      sending: false,
      form: {
        users: undefined,
        workcenters: undefined,
        workers: undefined,
        industry: '',
      },
    }
  },

  computed: {
    ...mapGetters(['tierUsage']),
  },

  mounted() {
    this.$bus.$on('open-upgrade-request', this.open)
  },

  beforeUnmount() {
    this.$bus.$off('open-upgrade-request', this.open)
  },

  methods: {
    // payload (optional): { users, workcenters, workers } to pre-fill. Falls back
    // to the tenant's current usage so the form opens with sensible estimates.
    open(payload) {
      const usage = this.tierUsage || {}
      const seed = payload || {}
      this.form = {
        users: this.pick(seed.users, usage.users),
        workcenters: this.pick(seed.workcenters, usage.workcenters),
        workers: this.pick(seed.workers, usage.workers),
        industry: '',
      }
      this.visible = true
    },

    pick(preferred, fallback) {
      const v = preferred != null ? preferred : fallback
      return typeof v === 'number' ? v : undefined
    },

    submit() {
      this.sending = true
      this.$http.post('v1/settings/account/upgrade-request', {
        users: this.form.users ?? null,
        workcenters: this.form.workcenters ?? null,
        workers: this.form.workers ?? null,
        industry: this.form.industry || null,
      })
        .then(({ data }) => {
          this.showApiSuccess(data)
          this.visible = false
        })
        .catch((error) => {
          this.showApiError(error)
        })
        .finally(() => {
          this.sending = false
        })
    },
  },
}
</script>

<style scoped>
.ur-description {
  margin: 0 0 16px 0;
  color: var(--el-text-color-regular, #606266);
  font-size: 13px;
  line-height: 1.5;
}

.ur-number {
  width: 100%;
}
</style>
